import React, { useState } from "react";
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential, sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../utils/firebase";
import AccountLayout from "../components/AccountLayout";

const inputCls = "w-full border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-ring text-foreground placeholder:text-[var(--color-muted-foreground)] bg-card text-sm transition-colors";
const labelCls = "block text-xs font-bold text-[var(--color-muted-foreground)] uppercase tracking-wider mb-1.5";

export default function Security() {
  const user = auth.currentUser;
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword,      setNewPassword]     = useState("");
  const [message,          setMessage]         = useState("");
  const [isError,          setIsError]         = useState(false);
  const [resetSent,        setResetSent]       = useState(false);
  const [loading,          setLoading]         = useState(false);
  const [resetLoading,     setResetLoading]     = useState(false);

  const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) { setMessage("Please fill in both fields."); setIsError(true); return; }
    if (!passwordRegex.test(newPassword)) {
      setMessage("New password must be at least 8 characters with an uppercase letter, number, and special character.");
      setIsError(true); return;
    }
    if (currentPassword === newPassword) { setMessage("New password must be different from your current password."); setIsError(true); return; }
    setLoading(true); setMessage("");
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, currentPassword));
      await updatePassword(user, newPassword);
      setMessage("Password updated successfully!"); setIsError(false);
      setCurrentPassword(""); setNewPassword("");
    } catch (err) {
      setMessage(`Error: ${err.message}`); setIsError(true);
    } finally { setLoading(false); }
  };

  const handleResetEmail = async () => {
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, user.email);
      setResetSent(true); setMessage("Password reset email sent to " + user.email); setIsError(false);
    } catch {
      setMessage("Failed to send reset email. Please try again."); setIsError(true);
    } finally { setResetLoading(false); }
  };

  return (
    <AccountLayout title="Security">
      <div className="max-w-lg space-y-6">
        {message && (
          <div className={`px-4 py-3 rounded-xl text-sm border ${isError ? "bg-red-50 border-red-200 text-red-700 dark:bg-red-950/30 dark:border-red-900/50 dark:text-red-400" : "bg-green-50 border-green-200 text-green-700 dark:bg-green-950/30 dark:border-green-900/50 dark:text-green-400"}`}>
            {message}
          </div>
        )}

        {/* Change password */}
        <div className="bg-card rounded-2xl border border-border shadow-sm p-7">
          <h3 className="font-bold text-foreground mb-5">Change Password</h3>
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div>
              <label className={labelCls}>Current Password</label>
              <input type="password" className={inputCls} placeholder="••••••••" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>New Password</label>
              <input type="password" className={inputCls} placeholder="Min. 8 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
            </div>
            <button type="submit" disabled={loading} className="w-full bg-brand-navy hover:bg-brand-amber disabled:opacity-50 transition-colors text-white font-bold py-3.5 rounded-xl">
              {loading ? "Updating…" : "Update Password"}
            </button>
          </form>
        </div>

        {/* Reset email */}
        <div className="bg-card rounded-2xl border border-border shadow-sm p-7">
          <h3 className="font-bold text-foreground mb-2">Forgot your password?</h3>
          <p className="text-sm text-[var(--color-muted-foreground)] mb-5">We'll send a reset link to <strong className="text-foreground">{user?.email}</strong>.</p>
          <button
            onClick={handleResetEmail}
            disabled={resetSent || resetLoading}
            className="w-full border-2 border-brand-navy text-brand-navy dark:border-accent dark:text-accent hover:bg-brand-navy hover:text-white dark:hover:bg-accent dark:hover:text-on-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-bold py-3 rounded-xl text-sm"
          >
            {resetSent ? "Reset Email Sent ✓" : resetLoading ? "Sending…" : "Send Password Reset Email"}
          </button>
        </div>
      </div>
    </AccountLayout>
  );
}
